import React, { useState, useCallback, useEffect, useRef } from 'react'
import { captureException } from '@sentry/react'
import { TFunction } from 'i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Modal } from '../../../../../components/common/Modal'
import { useAppTranslation } from '../../../../../hooks/useAppTranslation'
import { ModalHeader } from '../../../../../components/common/Modal/ModalHeader'
import { ModalBody } from '../../../../../components/common/Modal/ModalBody'
import { UploadBinderForm } from './UploadBinderForm'
import { ToastType } from '../../../../../components/common/Toast/toast-type'
import { useToastNotifications } from '../../../../../hooks/useToastNotification'
import { BinderType } from '../helper'
import { NBinder } from '../../../../../interfaces/services/binder.interfaces'
import { IBinderModel } from '../../../../../interfaces/models/binder.interfaces'
import {
  binderQueries,
  createBinderDocuments,
  deleteDocument,
} from '../../../../../api/binder'
import { theme } from '../../../../../theme/theme'

interface RejectedFile {
  filename: string
  id: string
  error: string
}

interface UploadProgress {
  totalBytes: number
  uploadedBytes: number
  rejectedFiles: RejectedFile[]
}

interface UploadBinderModalProps {
  rows: BinderType[]
  show: boolean
  close: () => void
  societyId: string
  folderId?: string
  published?: boolean
}

const checkFileType = (
  filename: string,
  mimeType: string,
  translate: TFunction<'translation', undefined>
): { valid: boolean; error?: string } => {
  const thumbnailMimeTypes = [
    'image/png',
    'image/jpeg',
    'image/jpg',
    'image/webp',
  ]
  const descriptionTypes = ['text/markdown', 'text/plain']

  if (filename.includes('folderThumbnail')) {
    if (!thumbnailMimeTypes.includes(mimeType)) {
      return {
        valid: false,
        error: translate('managementBinderView.uploadThumbnailInvalidType'),
      }
    }
    return { valid: true }
  }

  if (filename.includes('folderDescription')) {
    if (!descriptionTypes.includes(mimeType)) {
      return {
        valid: false,
        error: translate('managementBinderView.uploadDescriptionInvalidType'),
      }
    }
    return { valid: true }
  }

  return { valid: true }
}

const isServerHandledFile = (filename: string, name: string): boolean => {
  const markers = ['folderThumbnail', 'folderDescription']
  return markers.some(
    (marker) => filename.includes(marker) || name.includes(marker)
  )
}

const estimateBase64ByteSize = (base64: string): number => {
  const base64Data = base64.includes(',') ? base64.split(',')[1] : base64
  const padding = (base64Data.match(/=+$/) || [''])[0].length
  return Math.max(0, (base64Data.length * 3) / 4 - padding)
}

const formatBytes = (bytes: number): string => {
  if (bytes <= 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  const exponent = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1
  )
  const value = bytes / 1024 ** exponent
  return `${value.toFixed(exponent === 0 ? 0 : 1)} ${units[exponent]}`
}

const uploadBlobWithProgress = (
  url: string,
  blob: Blob,
  mimeType: string,
  onProgress: (loadedBytes: number) => void
): Promise<void> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    xhr.setRequestHeader('Content-Type', mimeType)
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress(event.loaded)
      }
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(blob.size)
        resolve()
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`))
      }
    }
    xhr.onerror = () => reject(new Error('Upload failed'))
    xhr.send(blob)
  })

export const UploadBinderModal: React.FC<UploadBinderModalProps> = ({
  rows,
  show,
  close,
  societyId,
  folderId,
  published,
}) => {
  const { translate } = useAppTranslation()
  const queryClient = useQueryClient()
  const { setToastNotification } = useToastNotifications()

  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({
    totalBytes: 0,
    uploadedBytes: 0,
    rejectedFiles: [],
  })
  const [isUploading, setIsUploading] = useState(false)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [uploadPhase, setUploadPhase] = useState<'preparing' | 'uploading'>(
    'preparing'
  )
  const uploadedBytesByFileRef = useRef<Record<string, number>>({})

  const resetUploadState = useCallback(() => {
    setUploadProgress({
      totalBytes: 0,
      uploadedBytes: 0,
      rejectedFiles: [],
    })
    setIsUploading(false)
    setUploadPhase('preparing')
  }, [])

  useEffect(() => {
    if (!isUploading) {
      setElapsedSeconds(0)
      return undefined
    }
    const startedAt = Date.now()
    setElapsedSeconds(0)
    const intervalId = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000))
    }, 1000)
    return () => clearInterval(intervalId)
  }, [isUploading])

  const formatElapsed = (totalSeconds: number): string => {
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  const base64ToBlob = async (base64: string): Promise<Blob> => {
    const response = await fetch(base64)
    return response.blob()
  }

  const { mutate: mutateDeleteDocument } = useMutation({
    mutationFn: async (params: { documentId: string }) => {
      await deleteDocument(params.documentId)
    },
  })

  const { mutateAsync: mutateCreateBinderDocumentsAsync } = useMutation({
    mutationFn: async (variables: {
      data: {
        mimeType: string
        name: string
        base64: string
        filename: string
        path?: string
      }[]
      societyId: string
      unitId: string
      notificationSettings: 'none' | 'notification'
    }): Promise<{
      response: Awaited<ReturnType<typeof createBinderDocuments>>
      rejectedFiles: RejectedFile[]
    }> => {
      const {
        data,
        societyId: _societyId,
        unitId,
        notificationSettings,
      } = variables
      const files = data.map((_file) => {
        const doc: NBinder.NCreate.IRequestBody = {
          name: _file.name,
          filename: _file.filename,
          societyId: _societyId,
          type: _file.filename.split('.').pop() as IBinderModel['type'],
          path: _file.path,
        }
        if (isServerHandledFile(_file.filename, _file.name)) {
          doc.data = _file.base64
        }
        return doc
      })
      const payload = { data: files, folderId, notificationSettings }
      const result = await createBinderDocuments(_societyId, unitId, payload)

      setUploadPhase('uploading')

      const urls = result.data.data
      const rejectedFiles: RejectedFile[] = []

      // Upload each file to S3 using the presigned URL
      if (urls && Array.isArray(urls)) {
        await Promise.all(
          urls.map(async (urlData, index) => {
            const { url, name, mimeType, id } = urlData
            const fileKey = `${unitId}-${index}`
            const estimatedSize = data[index]?.base64
              ? estimateBase64ByteSize(data[index].base64)
              : 0
            const creditSkippedBytes = (): void => {
              uploadedBytesByFileRef.current[fileKey] = estimatedSize
              setUploadProgress((prev) => ({
                ...prev,
                uploadedBytes: prev.uploadedBytes + estimatedSize,
              }))
            }
            const { valid, error: invalidTypeError } = checkFileType(
              name,
              mimeType,
              translate
            )
            if (!valid) {
              rejectedFiles.push({
                filename: name,
                id,
                error: invalidTypeError || 'Invalid file type',
              })
              creditSkippedBytes()
              return
            }
            const filename = name || 'Unknown file'
            if (!url) {
              creditSkippedBytes()
              return
            }
            const localBase64 = data[index]?.base64
            if (!localBase64) {
              rejectedFiles.push({
                filename,
                id,
                error: 'Missing file data for upload',
              })
              mutateDeleteDocument({ documentId: id })
              return
            }
            const blobObject = await base64ToBlob(localBase64)
            try {
              await uploadBlobWithProgress(
                url,
                blobObject,
                mimeType,
                (loadedBytes) => {
                  const previousLoaded =
                    uploadedBytesByFileRef.current[fileKey] ?? 0
                  const delta = loadedBytes - previousLoaded
                  uploadedBytesByFileRef.current[fileKey] = loadedBytes
                  if (delta !== 0) {
                    setUploadProgress((prev) => ({
                      ...prev,
                      uploadedBytes: prev.uploadedBytes + delta,
                    }))
                  }
                }
              )
            } catch (error) {
              const errorMessage =
                error instanceof Error ? error.message : 'Upload failed'
              rejectedFiles.push({
                filename,
                id: urlData.id,
                error: errorMessage,
              })
              // Attempt to delete the document record if the upload fails
              mutateDeleteDocument({ documentId: id })

              const previousLoaded =
                uploadedBytesByFileRef.current[fileKey] ?? 0
              const remaining = blobObject.size - previousLoaded
              if (remaining > 0) {
                uploadedBytesByFileRef.current[fileKey] = blobObject.size
                setUploadProgress((prev) => ({
                  ...prev,
                  uploadedBytes: prev.uploadedBytes + remaining,
                }))
              }
            }
          })
        )
      }

      return { response: result, rejectedFiles }
    },
  })

  const handleSubmit = async (data: {
    files: {
      mimeType: string
      name: string
      base64: string
      filename: string
      path?: string
    }[]
    notificationSettings: 'none' | 'notification'
    rejectedFiles?: { filename: string; error: string }[]
  }): Promise<void> => {
    setIsUploading(true)
    setUploadPhase('preparing')
    uploadedBytesByFileRef.current = {}
    const totalBytes =
      data.files.reduce(
        (sum, file) => sum + estimateBase64ByteSize(file.base64),
        0
      ) * rows.length
    setUploadProgress({
      totalBytes,
      uploadedBytes: 0,
      rejectedFiles: [],
    })

    try {
      const results = await Promise.all(
        rows.map((row) =>
          mutateCreateBinderDocumentsAsync({
            data: data.files,
            notificationSettings: data.notificationSettings,
            societyId,
            unitId: row.unitId,
          })
        )
      )

      const dropZoneRejectedFiles: RejectedFile[] = (
        data.rejectedFiles ?? []
      ).map((file, index) => ({
        filename: file.filename,
        id: `dropzone-${index}`,
        error: file.error,
      }))
      const allRejectedFiles = [
        ...dropZoneRejectedFiles,
        ...results.flatMap((result) => result.rejectedFiles),
      ]

      setIsUploading(false)
      queryClient.invalidateQueries({
        queryKey: binderQueries.getAllBinders(societyId).queryKey,
      })

      if (allRejectedFiles.length > 0) {
        setUploadProgress((prev) => ({
          ...prev,
          rejectedFiles: allRejectedFiles,
        }))
      } else {
        setToastNotification(
          ToastType.SUCCESS,
          translate('common.form.upload.success')
        )
        close()
      }
    } catch {
      setIsUploading(false)
    }
  }

  const onError = (error: unknown): void => {
    captureException(error)
  }

  const handleClose = useCallback(() => {
    if (!isUploading) {
      resetUploadState()
      close()
    }
  }, [isUploading, resetUploadState, close])

  const progressPercentage =
    uploadPhase === 'uploading' && uploadProgress.totalBytes > 0
      ? Math.min(
          100,
          Math.round(
            (uploadProgress.uploadedBytes / uploadProgress.totalBytes) * 100
          )
        )
      : 0

  const showProgressUI = isUploading || uploadProgress.rejectedFiles.length > 0

  const notificationSettingsOptions: {
    value: 'none' | 'notification'
    label: string
  }[] = [
    {
      value: 'none',
      label: translate('common.notificationSettings.none'),
    },
    {
      value: 'notification',
      label: translate('common.notificationSettings.notification'),
    },
  ]

  return (
    <Modal show={show}>
      {{
        header: (
          <ModalHeader onClose={handleClose}>
            {translate('managementBinderView.addNewDocument')}
          </ModalHeader>
        ),
        body: (
          <ModalBody>
            {showProgressUI ? (
              <div className="flex flex-col gap-4">
                {isUploading && (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <span style={theme.textVariants.base}>
                        {translate(
                          uploadPhase === 'preparing'
                            ? 'common.form.upload.preparing'
                            : 'common.form.upload.uploading'
                        )}
                      </span>
                      {uploadPhase === 'uploading' && (
                        <span
                          style={theme.textVariants.baseBold}
                        >{`${formatBytes(
                          uploadProgress.uploadedBytes
                        )}/${formatBytes(uploadProgress.totalBytes)}`}</span>
                      )}
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                      <div
                        className="h-full rounded-full bg-brandGreen transition-all duration-300 ease-out"
                        style={{
                          width: `${progressPercentage}%`,
                        }}
                      />
                    </div>
                    <span
                      style={theme.textVariants.caption}
                      className="text-center text-gray-500"
                    >
                      {uploadPhase === 'preparing'
                        ? formatElapsed(elapsedSeconds)
                        : `${progressPercentage}% · ${formatElapsed(
                            elapsedSeconds
                          )}`}
                    </span>
                  </div>
                )}
                {uploadProgress.rejectedFiles.length > 0 && (
                  <div className="flex flex-col gap-2">
                    <span
                      style={theme.textVariants.baseBold}
                      className="text-red-600"
                    >
                      {translate('common.form.upload.failedFiles')}
                    </span>
                    <div className="max-h-40 overflow-y-auto rounded border border-red-200 bg-red-50 p-2">
                      {uploadProgress.rejectedFiles.map((file) => (
                        <div
                          key={`rejected-${file.id}-${file.filename}`}
                          className="flex flex-col py-1"
                        >
                          <span style={theme.textVariants.base}>
                            {file.filename}
                          </span>
                          <span
                            style={theme.textVariants.caption}
                            className="text-red-500"
                          >
                            {file.error}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {!isUploading && (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={handleClose}
                      className="rounded bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
                    >
                      {translate('common.actions.close')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <UploadBinderForm
                onClose={handleClose}
                onSubmit={handleSubmit}
                loading={isUploading}
                onError={onError}
                unitIds={rows.map((row) => row.unitId)}
                folderId={folderId}
                optionSettings={notificationSettingsOptions}
                published={published}
              />
            )}
          </ModalBody>
        ),
      }}
    </Modal>
  )
}
